import { useState } from 'react';
import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar } from '../components/primitives';
import { SideModal, LabeledText, LabeledSelect } from '../components/SideModal';
import { accessRoleBadge } from '../lib/status';
import { can } from '../lib/rbac';
import { av } from '../lib/avatar';
import { api } from '../lib/api';
import type { AccessEntry, Integration, Role, Setting } from '../../shared/types';

const CURRENCY_OPTS = ['USD ($)', 'EUR (€)', 'GBP (£)', 'CAD (C$)', 'AUD (A$)'].map((v) => ({ label: v, value: v }));
const TZ_OPTS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
  'Europe/London',
].map((v) => ({ label: v, value: v }));

interface RoleDef { role: string; desc: string; icon: string }
interface SettingsData {
  org: Setting[];
  roleDefs: RoleDef[];
  access: AccessEntry[];
  integrations: Integration[];
}

const ROLE_OPTS: Role[] = ['super_admin', 'admin', 'manager', 'staff'];

export function Settings() {
  const { refresh, user } = useApp();
  const { data } = useFetch<SettingsData>('/settings');
  const [editOrg, setEditOrg] = useState(false);
  if (!data) return null;

  const canEditOrg = user ? can(user.role, 'update', 'settings') : false;

  const changeRole = async (id: number, role: string) => {
    await api.patch(`/users/${id}`, { role });
    refresh();
  };
  const removeUser = async (e: AccessEntry) => {
    if (!confirm(`Remove ${e.name}'s access? They will no longer be able to sign in.`)) return;
    try {
      await api.del(`/users/${e.id}`);
      refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not remove user.');
    }
  };
  const toggleActive = async (e: AccessEntry) => {
    await api.patch(`/users/${e.id}`, { status: e.status === 'active' ? 'suspended' : 'active' });
    refresh();
  };
  const invite = async () => {
    const email = prompt('Email address to invite:');
    if (!email) return;
    await api.post('/users', { email, name: email.split('@')[0], role: 'manager' });
    refresh();
  };

  return (
    <Fade>
      <Card style={{ padding: 20, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Organization</div>
          <div style={{ flex: 1 }} />
          {canEditOrg && <Button variant="secondary" icon="edit" onClick={() => setEditOrg(true)} style={{ padding: '6px 12px' }}>Edit</Button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {data.org.map((s) => (
            <div key={s.key}>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 5 }}>{labelFor(s.key)}</div>
              <div style={{ border: '1px solid var(--border-2)', borderRadius: 8, padding: '9px 11px', fontSize: 13 }}>{s.value}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ padding: 20, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Access & permissions</div>
          <div style={{ flex: 1 }} />
          <Button icon="add" onClick={invite} style={{ padding: '6px 12px' }}>Invite admin</Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${data.roleDefs.length}, 1fr)`, gap: 10, marginBottom: 18 }}>
          {data.roleDefs.map((r) => (
            <div key={r.role} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 13 }}>
              <Icon name={r.icon} size={22} color="var(--accent)" />
              <div style={{ fontWeight: 600, fontSize: 13, margin: '8px 0 4px' }}>{r.role}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.45 }}>{r.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2.4fr 1.4fr 0.8fr 40px', gap: 14, padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' }}>
            <div>Person</div><div>Access level</div><div>Active</div><div />
          </div>
          {data.access.map((e) => {
            const a = av(e.name, e.avatar_kind);
            const badge = accessRoleBadge(e.role);
            const isSelf = user?.id === e.id;
            return (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2.4fr 1.4fr 0.8fr 40px', gap: 14, padding: '11px 14px', borderTop: '1px solid var(--border)', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                  <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={32} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{e.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{e.email}</div>
                  </div>
                </div>
                <div>
                  <select value={e.role} onChange={(ev) => changeRole(e.id, ev.target.value)} style={{ appearance: 'none', border: '1px solid var(--border-2)', background: badge.bg, color: badge.fg, borderRadius: 20, padding: '4px 12px', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                    {ROLE_OPTS.map((r) => <option key={r} value={r} style={{ color: 'var(--text)', background: 'var(--surface)' }}>{accessRoleBadge(r).label}</option>)}
                  </select>
                </div>
                <div>
                  <div onClick={() => toggleActive(e)} className="cc-clickable" style={{ width: 38, height: 22, borderRadius: 20, background: e.status === 'active' ? 'var(--accent)' : 'var(--surface-3)', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 2, [e.status === 'active' ? 'right' : 'left']: 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'all .15s' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {isSelf ? (
                    <span title="You can't remove your own account" style={{ display: 'inline-flex' }}><Icon name="lock" size={16} color="var(--text-3)" /></span>
                  ) : (
                    <span onClick={() => removeUser(e)} title="Remove access" className="cc-clickable" style={{ display: 'inline-flex' }}><Icon name="delete" size={18} color="var(--text-3)" /></span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card style={{ padding: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Connected integrations</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.integrations.map((g) => {
            const tone = g.status === 'connected' ? ['var(--success-bg)', 'var(--success)', 'Connected'] : ['var(--warning-bg)', 'var(--warning)', 'Action needed'];
            return (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                <div className="cc-mono" style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: 'var(--text-2)' }}>{g.mark}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{g.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{g.last_sync}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: tone[0], color: tone[1], display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone[1] }} />{tone[2]}
                </span>
                <Button variant="secondary" disabled title="OAuth connect & sync arrives in Phase 2" style={{ padding: '6px 12px' }}>Manage</Button>
              </div>
            );
          })}
        </div>
      </Card>

      {editOrg && <OrgEditor org={data.org} onClose={() => setEditOrg(false)} onSaved={() => { refresh(); setEditOrg(false); }} />}
    </Fade>
  );
}

function OrgEditor({ org, onClose, onSaved }: { org: Setting[]; onClose: () => void; onSaved: () => void }) {
  const initial = Object.fromEntries(org.map((s) => [s.key, s.value]));
  const [orgName, setOrgName] = useState(initial.org_name || '');
  const [currency, setCurrency] = useState(initial.currency || CURRENCY_OPTS[0].value);
  const [timezone, setTimezone] = useState(initial.timezone || TZ_OPTS[0].value);
  const [domain, setDomain] = useState(initial.workspace_domain || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Pull in any stored value not already in the option lists so a save can't drop it.
  const currencyOpts = CURRENCY_OPTS.some((o) => o.value === currency) ? CURRENCY_OPTS : [{ label: currency, value: currency }, ...CURRENCY_OPTS];
  const tzOpts = TZ_OPTS.some((o) => o.value === timezone) ? TZ_OPTS : [{ label: timezone, value: timezone }, ...TZ_OPTS];

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.put('/settings', { org_name: orgName, currency, timezone, workspace_domain: domain });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save settings.');
      setBusy(false);
    }
  };

  return (
    <SideModal
      title="Organization settings"
      sub="Name, currency, timezone & workspace domain"
      onClose={onClose}
      footer={
        <>
          <span />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || !orgName}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledText label="Organization name" value={orgName} onChange={setOrgName} placeholder="e.g. CCSilo" />
      <LabeledText label="Workspace domain" value={domain} onChange={setDomain} placeholder="e.g. ccsilo.team" mono />
      <LabeledSelect label="Primary currency" value={currency} onChange={setCurrency} options={currencyOpts} />
      <LabeledSelect label="Timezone" value={timezone} onChange={setTimezone} options={tzOpts} />
      {err && <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 4 }}>{err}</div>}
    </SideModal>
  );
}

function labelFor(key: string): string {
  const m: Record<string, string> = {
    org_name: 'Organization name',
    currency: 'Primary currency',
    timezone: 'Timezone',
    workspace_domain: 'Workspace domain',
  };
  return m[key] || key;
}
